import { H7, useTheme, useWindowProperties } from '@qred/components-library';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ConfirmationCard } from '~/components/shared/ConfirmationCard/ConfirmationCard';
import Header from '~/components/shared/Header/Header';
import constVars from '~/constants/constVars';
import { getHomePageByMarket } from '~/constants/markets';
import { ApplicationStatus } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { getCookieValue } from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { accessToken } from '~/store/apis/utils/accessToken';
import {
  clearLoanApplicationState,
  updateApplicationStatus,
} from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  GoldenRatioContainer,
  StandardPageContainer,
} from '~/styles/BaseStyledComponents';
import { ProcessingCard } from './components/ProcessingCard';

export const ConfirmationPage = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const websocket = useRef<WebSocket | null>(null);
  const {
    loanApplication: { applicationStatus, form, apiStatus, userId },
    intl: { market, language },
  } = useSelector((state: RootState) => state);
  const { isMobile } = useWindowProperties();

  const applicationUuid = form.applicationUuid;

  const isApproved = applicationStatus === ApplicationStatus.OfferApproved;
  const isNotApproved =
    applicationStatus === ApplicationStatus.OfferNotApproved;
  const isProcessing =
    applicationStatus === ApplicationStatus.ApplicationUnderReview ||
    applicationStatus === ApplicationStatus.ApplicationFinished;

  const onClickGoToWebsite = () => {
    if (userId) {
      dispatch(clearLoanApplicationState());
      history.push('/');
    } else {
      window.location.assign(getHomePageByMarket(market, language));
    }
  };

  const onClickViewOffer = () => {
    const savedApplicationUuid = applicationUuid;
    dispatch(clearLoanApplicationState());
    history.push(`/${constVars.ROUTE_LOAN_OFFER}/${savedApplicationUuid}`);
  };

  useEffect(() => {
    if (
      apiStatus.patch === ApiStatus.Completed &&
      applicationStatus === ApplicationStatus.ApplicationFinished
    ) {
      dispatch(
        updateApplicationStatus(ApplicationStatus.ApplicationUnderReview)
      );
    }
  }, [apiStatus.patch, applicationStatus]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.ApplicationUnderReview) {
      websocket.current = new WebSocket(
        import.meta.env.VITE_WEBSOCKET_ONBOARDING_URL
      );

      websocket.current.onopen = async () => {
        setErrorMessage(null);
        const userData = {
          action: 'authorization',
          accessToken: form.applicantAuthenticated
            ? accessToken.get()
            : getCookieValue('publicToken'),
          callbackId: applicationUuid,
          countryCode: market.toLowerCase(),
        };
        if (websocket.current) {
          websocket.current && websocket.current.send(JSON.stringify(userData));
        }
      };

      websocket.current.onerror = (error: Event) => {
        logToSentry(error, 'websocket onerror', {});

        setErrorMessage(t('Onboarding.Disconnected') as string);
      };

      websocket.current.onmessage = (event: MessageEvent) => {
        const messageData = JSON.parse(event.data);
        try {
          if (messageData?.message) {
            setErrorMessage(null);
            if (messageData.message.offerExist) {
              dispatch(
                updateApplicationStatus(ApplicationStatus.OfferApproved)
              );
              dispatch(pushToGtmLoanAction({ actionName: 'auto_approved' }));
            } else {
              dispatch(
                updateApplicationStatus(ApplicationStatus.OfferNotApproved)
              );
              dispatch(
                pushToGtmLoanAction({ actionName: 'not_auto_approved' })
              );
            }
          }
        } catch (error) {
          logToSentry(error, 'websocket try/catch error', {});
          setErrorMessage(t('ApiFailureError.ErrorMessageTitle') as string);
          console.error(error);
        }
      };
    }
  }, [applicationStatus]);

  useEffect(() => {
    return () => {
      if (websocket?.current) {
        websocket.current.close();
      }
    };
  }, []);

  return (
    <StandardPageContainer
      style={{
        backgroundColor: theme.fn.themeColor('Page/Option_2_(Green)'),
      }}
    >
      <Header />
      <GoldenRatioContainer $grow={isMobile && !isProcessing}>
        {!errorMessage ? (
          <>
            {isProcessing && <ProcessingCard />}

            {isApproved && (
              <ConfirmationCard
                header={t('Onboarding.Congratulations') as string}
                text={
                  t('Onboarding.PreApprovedApplicationDescription') as string
                }
                buttonText={t('Onboarding.ViewOffer') as string}
                onButtonClick={onClickViewOffer}
                headerImgSrc="/congratulations_qrayon.png"
              />
            )}

            {isNotApproved && (
              <ConfirmationCard
                header={t('Onboarding.ApplicationSubmitted') as string}
                text={
                  t(
                    'Onboarding.ApplicationSubmittedDescriptionNotAutoApproved'
                  ) as string
                }
                buttonText={t('Onboarding.GoToQredWebsite') as string}
                onButtonClick={onClickGoToWebsite}
              />
            )}
          </>
        ) : (
          <H7>{errorMessage}</H7>
        )}
      </GoldenRatioContainer>
    </StandardPageContainer>
  );
};
